import React from 'react';
import { ArrowRight, Crosshair, Clock3, Monitor, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import './deadcenter.css';

/* ─────────────────────────────────────────────────────────────────
   Dead Center by CubitPlay — Landing Page
   Mirrors the Perfect Slice / CubitPlay homepage layout & CTA style.
   Uses existing Dead Center teal brand tokens (.dch-* prefix)
   ───────────────────────────────────────────────────────────────── */

/* ── Crosshair / target logo mark ───────────────────────────────── */
const DcMark = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
    className="dch-mark-svg"
  >
    <circle cx="16" cy="16" r="13" stroke="#0F766E" strokeWidth="1.5"/>
    <circle cx="16" cy="16" r="5.5" stroke="#0F766E" strokeWidth="1.5"/>
    <circle cx="16" cy="16" r="2.5" fill="#F72585"/>
    <line x1="16" y1="1"   x2="16" y2="9.5"  stroke="#0F766E" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="16" y1="22.5" x2="16" y2="31"  stroke="#0F766E" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="1"  y1="16"  x2="9.5" y2="16"  stroke="#0F766E" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="22.5" y1="16" x2="31" y2="16" stroke="#0F766E" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

/* ── Hero illustration: polygon + centroid reveal ────────────────── */
function DcHeroArt() {
  return (
    <div
      className="dch-art"
      role="img"
      aria-label="An irregular polygon shape. A teal dot marks where the player tapped; a vivid pink crosshair marks the true mathematical center."
      data-testid="dc-hero-art"
    >
      <div className="dch-art-caption">
        <span className="dch-art-dot" />
        TAP THE CENTER. ONE SHOT.
      </div>

      <svg viewBox="0 0 440 320" className="dch-art-svg" aria-hidden="true">
        <defs>
          <linearGradient id="dch-poly-g" x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0" stopColor="#99F6E4"/>
            <stop offset="1" stopColor="#5EEAD4"/>
          </linearGradient>
          <filter id="dch-poly-shadow" x="-30%" y="-30%" width="160%" height="180%">
            <feDropShadow dx="0" dy="10" stdDeviation="9" floodColor="#0F766E" floodOpacity=".12"/>
          </filter>
        </defs>

        {/* Guide circles — decorative geometry-lab feel */}
        <circle cx="225" cy="158" r="145" fill="none" stroke="#CCFBF1" strokeDasharray="2 7" strokeWidth="1.2"/>
        <circle cx="225" cy="158" r="88"  fill="none" stroke="#CCFBF1" strokeDasharray="2 7" strokeWidth="0.8"/>

        {/* Ground shadow */}
        <ellipse cx="225" cy="300" rx="108" ry="9" fill="#D1FAE5" opacity=".5"/>

        {/* Main polygon — irregular hexagon */}
        <g filter="url(#dch-poly-shadow)">
          <polygon
            points="82,176 140,62 302,54 376,148 329,262 118,268"
            fill="url(#dch-poly-g)"
            stroke="#0F766E"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          {/* Specular highlight overlay */}
          <polygon
            points="82,176 140,62 302,54 376,148 329,262 118,268"
            fill="white"
            fillOpacity=".07"
          />
        </g>

        {/* Distance line: tap marker → centroid */}
        <line
          x1="254" y1="136" x2="222" y2="160"
          stroke="#F72585" strokeWidth="1.2" strokeDasharray="4 4" opacity=".6"
        />

        {/* Player tap marker: white fill + teal ring */}
        <circle cx="254" cy="136" r="11" fill="rgba(20,184,166,0.14)"/>
        <circle cx="254" cy="136" r="7"  fill="#fff" stroke="#14B8A6" strokeWidth="2"/>
        <circle cx="254" cy="136" r="2.5" fill="#0F766E"/>

        {/* True centroid: pink halo + crosshair + dot */}
        <circle cx="222" cy="160" r="13" fill="none" stroke="rgba(247,37,133,0.2)" strokeWidth="5.5"/>
        <path d="M208 160H236M222 147V173" stroke="#F72585" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="222" cy="160" r="4.5" fill="#F72585" stroke="#fff" strokeWidth="2"/>

        {/* Decorative corner marks */}
        <path d="M32 38h12m-6-6v12M400 38h12m-6-6v12M32 278h12m-6-6v12" stroke="#CCFBF1" strokeWidth="1.5"/>
      </svg>

      {/* Score sticker */}
      <div className="dch-score-sticker" data-testid="dc-score-sticker">
        <span>THIS SHAPE</span>
        <strong>87</strong>
        <small>/100</small>
      </div>

      {/* Annotation arrow + label pointing at centroid */}
      <div className="dch-art-note" aria-hidden="true">
        <svg viewBox="0 0 72 34" fill="none">
          <path d="M68 28Q14 38 12 5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>dead center.</span>
      </div>
    </div>
  );
}

/* ── Main landing page component ─────────────────────────────────── */
export function DeadCenterHome({ onPlay }) {
  return (
    <main id="dc-main-content" className="dch-page" data-testid="dead-center-home">

      {/* ────────────────── HERO ────────────────── */}
      <section className="dch-hero cp-container" aria-labelledby="dch-home-title">
        <div className="dch-hero-copy">

          {/* Logo lockup: mark + wordmark + by CubitPlay */}
          <div className="dch-lockup" data-testid="dead-center-home-lockup">
            <DcMark />
            <span className="dch-wordmark" data-testid="dead-center-wordmark">Dead Center</span>
            <a href="/" className="dch-by-cubit" data-testid="dc-cubitplay-link">
              by <b>CubitPlay</b>
            </a>
          </div>

          <h1 id="dch-home-title" data-testid="dead-center-home-heading">
            One tap.<br/>
            <span className="dch-accent">Dead center.</span>
          </h1>

          <p className="dch-hero-desc" data-testid="dead-center-home-description">
            5 shapes. One tap each. Find the center.<br/>
            Get close. Then get closer.
          </p>

          <div className="dch-play-row">
            <button
              className="dch-play-btn"
              data-testid="dead-center-home-play-button"
              onClick={onPlay}
            >
              <Target size={18} aria-hidden="true"/>
              PLAY TODAY'S DEAD CENTER
              <ArrowRight size={19} aria-hidden="true"/>
            </button>
            <span className="dch-play-note" data-testid="dc-home-play-note">
              <span className="dch-dot-note" aria-hidden="true"/>
              No signup. Just tap.
            </span>
          </div>
        </div>

        <DcHeroArt />
      </section>

      {/* ────────────────── HOW TO PLAY ────────────────── */}
      <section className="dch-feature cp-container" aria-labelledby="dch-feature-title">
        <div className="dch-section-label">
          <span className="dch-eyebrow" data-testid="dc-feature-eyebrow">HOW TO PLAY</span>
          <span className="dch-rule" aria-hidden="true"/>
          <span className="dch-index" aria-hidden="true">01 / DEAD CENTER</span>
        </div>

        <div className="dch-feature-card" data-testid="dead-center-how-to-play-card">

          {/* Left column: copy + CTA */}
          <div className="dch-feature-copy">
            <Badge variant="outline" className="dch-badge" data-testid="dc-feature-badge">
              <Crosshair size={12} aria-hidden="true"/> DEAD CENTER
            </Badge>

            <h2 id="dch-feature-title" data-testid="dc-feature-heading">
              Find the<br/>mathematical center.
            </h2>

            <p data-testid="dc-feature-description">
              Each day brings 5 new shapes. Tap where you think the centroid is.
              The hot-pink marker reveals exactly where dead center was.
              <br/><strong>One tap per shape. Your accuracy is your score.</strong>
            </p>

            <div className="dch-meta" data-testid="dc-feature-meta">
              <span><Clock3 size={13} aria-hidden="true"/> ~2 min</span>
              <span><Target size={13} aria-hidden="true"/> Precision</span>
              <span><Monitor size={13} aria-hidden="true"/> Play in browser</span>
            </div>

            <button
              className="dch-play-btn dch-feature-play-btn"
              data-testid="dead-center-feature-play-now-button"
              onClick={onPlay}
            >
              PLAY NOW
              <ArrowRight size={18} aria-hidden="true"/>
            </button>
          </div>

          {/* Right column: 3 game rules */}
          <div className="dch-steps-panel" data-testid="dc-steps-panel">
            {[
              {
                icon: '5',
                title: '5 shapes per challenge',
                desc: 'Each round harder than the last — from simple to complex.',
              },
              {
                icon: '✕',
                title: 'One tap per shape',
                desc: 'No retries. Commit to your instinct and tap.',
              },
              {
                icon: '500',
                title: 'Up to 500 points',
                desc: 'Closer to the centroid = higher score. Max 100 pts per shape.',
              },
            ].map(({ icon, title, desc }, i) => (
              <div key={i} className="dch-step" data-testid={`dc-step-${i + 1}`}>
                <span className="dch-step-icon" aria-hidden="true">{icon}</span>
                <div>
                  <strong className="dch-step-title">{title}</strong>
                  <p className="dch-step-desc">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}
